import { Fragment, JSXElementConstructor, useMemo, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import type { CustomGlyphProps, GlyphProps } from "@atlaskit/icon/types";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import {
    USER_SEGMENTATION_TEAM_TYPE_DESIGN,
    USER_SEGMENTATION_TEAM_TYPE_FINANCE,
    USER_SEGMENTATION_TEAM_TYPE_HR,
    USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT,
    USER_SEGMENTATION_TEAM_TYPE_LEGAL,
    USER_SEGMENTATION_TEAM_TYPE_MARKETING,
    USER_SEGMENTATION_TEAM_TYPE_OPERATIONS,
    USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL,
    USER_SEGMENTATION_TEAM_TYPE_SALES,
    USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
    USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
    USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
    UserSegmentationTeamTypes,
    USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE,
    USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT
} from "../../models/user-segmentation.ts";

import { UserSegCard } from "./card.tsx";
import {
    SoftwareDevelopmentIcon2,
    ProductManagementIcon,
    MarketingIcon2,
    DesignIcon2,
    ProjectManagementIcon,
    OperationsIcon2,
    ITSupportIcon2,
    HumanResourcesIcon,
    CustomerServiceIcon2,
    LegalIcon2,
    FinanceIcon2,
    SalesIcon2,
    DataScienceIcon,
    OtherIcon
} from "./custom-icon.tsx";

type CardsMapObject = {
    readonly card: string;
    readonly Icon:
        | JSXElementConstructor<GlyphProps>
        | JSXElementConstructor<CustomGlyphProps>;
    readonly teamType: UserSegmentationTeamTypes;
    readonly isDefaultTemplate?: boolean;
};

export const CardsGrid = () => {
    const intl = useIntl();

    const cardsMap: Array<CardsMapObject> = useMemo(
        () => [
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.softwareDevelopment",
                    defaultMessage: "Software development"
                }),
                Icon: SoftwareDevelopmentIcon2,
                teamType: USER_SEGMENTATION_TEAM_TYPE_SOFTWARE
            },
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.productManagement",
                    defaultMessage: "Product management"
                }),
                Icon: ProductManagementIcon,
                teamType: USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT
            },
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.marketing",
                    defaultMessage: "Marketing"
                }),
                Icon: MarketingIcon2,
                teamType: USER_SEGMENTATION_TEAM_TYPE_MARKETING
            },
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.design",
                    defaultMessage: "Design"
                }),
                Icon: DesignIcon2,
                teamType: USER_SEGMENTATION_TEAM_TYPE_DESIGN
            },
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.projectManagement",
                    defaultMessage: "Project management"
                }),
                Icon: ProjectManagementIcon,
                teamType: USER_SEGMENTATION_TEAM_TYPE_PROJECT_MANAGEMENT
            },
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.operations",
                    defaultMessage: "Operations"
                }),
                Icon: OperationsIcon2,
                teamType: USER_SEGMENTATION_TEAM_TYPE_OPERATIONS
            },
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.itSupport",
                    defaultMessage: "IT support"
                }),
                Icon: ITSupportIcon2,
                teamType: USER_SEGMENTATION_TEAM_TYPE_IT_SUPPORT
            },
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.humanResources",
                    defaultMessage: "Human resources"
                }),
                Icon: HumanResourcesIcon,
                teamType: USER_SEGMENTATION_TEAM_TYPE_HR
            },
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.customerService",
                    defaultMessage: "Customer service"
                }),
                Icon: CustomerServiceIcon2,
                teamType: USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE
            },
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.legal",
                    defaultMessage: "Legal"
                }),
                Icon: LegalIcon2,
                teamType: USER_SEGMENTATION_TEAM_TYPE_LEGAL
            },
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.finance",
                    defaultMessage: "Finance"
                }),
                Icon: FinanceIcon2,
                teamType: USER_SEGMENTATION_TEAM_TYPE_FINANCE
            },
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.sales",
                    defaultMessage: "Sales"
                }),
                Icon: SalesIcon2,
                teamType: USER_SEGMENTATION_TEAM_TYPE_SALES
            },
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.dataScience",
                    defaultMessage: "Data science"
                }),
                Icon: DataScienceIcon,
                teamType: USER_SEGMENTATION_TEAM_TYPE_DATA_SCIENCE
            },
            {
                card: intl.formatMessage({
                    id: "UserSeg.teamTypeSelection.otherOrPersonal",
                    defaultMessage: "Other"
                }),
                Icon: OtherIcon,
                teamType: USER_SEGMENTATION_TEAM_TYPE_OTHER_PERSONAL
            }
        ],
        []
    );

    const [cardSelected, setCardSelected] = useState(false);
    const onCardClick = () => {
        setCardSelected(true);
    };
    const afterCardClick = () => {
        setCardSelected(false);
    };

    const isResponsive = checkFeatureFlagGate(
        featureFlags.cfindTeamFocusResponsive
    );

    return (
        <div css={[gridContainerBase, isResponsive && gridContainerResponsive]}>
            {cardsMap.map((cardObject, index) => {
                return (
                    <Fragment key={index}>
                        <UserSegCard
                            idx={index}
                            {...cardObject}
                            onClick={onCardClick}
                            afterClick={afterCardClick}
                            disabled={cardSelected}
                        />
                    </Fragment>
                );
            })}
        </div>
    );
};

const gridContainerBase = css({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: token("space.150"),
    [media.above.sm]: {
        columnGap: token("space.200"),
        overflow: "auto",
        paddingBlockStart: "1px",
        display: "grid",
        gridTemplateColumns: "1fr 1fr"
    }
});

const gridContainerResponsive = css({
    display: "grid",
    gridTemplateColumns: "1fr",
    boxSizing: "border-box",
    padding: "1px",
    justifyItems: "center",
    [media.above.sm]: {
        justifyItems: "stretch",
        gridTemplateColumns: "1fr 1fr"
    }
});
